package features.payment

import features.booking.SmartVanColors
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Store
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.material.icons.filled.CheckCircle
import features.booking.BookingViewModel

@Composable
fun OxxoConfirmationScreen(
    bookingViewModel: BookingViewModel,
    paymentViewModel: PaymentViewModel,
    onFinishClick: () -> Unit
) {
    val oxxoResult by paymentViewModel.oxxoResult.collectAsState()
    val pagoConfirmado by paymentViewModel.pagoConfirmado.collectAsState()

    val totalToPay = oxxoResult?.monto ?: 0

    // Iniciar escucha de confirmación cuando se muestra la pantalla
    LaunchedEffect(oxxoResult?.paymentIntentId) {
        val piId = oxxoResult?.paymentIntentId
        if (!piId.isNullOrBlank()) {
            paymentViewModel.escucharConfirmacionPago(piId)
        }
    }

    DisposableEffect(Unit) {
        onDispose { paymentViewModel.detenerEscuchaPago() }
    }

    val oxxoOrange = SmartVanColors.Naranja
    val smartvanNavy = SmartVanColors.AzulOscuro
    val smartvanGreen = SmartVanColors.Verde

    // FIX #15: Scrollable para pantallas pequeñas
    Column(
        modifier = Modifier.fillMaxSize().padding(24.dp).verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            Icons.Filled.Store,
            contentDescription = null,
            tint = oxxoOrange,
            modifier = Modifier.size(48.dp)
        )
        Spacer(modifier = Modifier.height(8.dp))
        Text(
            "Pago en OXXO",
            style = MaterialTheme.typography.displaySmall,
            fontWeight = FontWeight.Black,
            color = smartvanNavy
        )
        Text(
            "Presenta esta referencia en cualquier tienda OXXO para completar tu pago.",
            textAlign = TextAlign.Center,
            color = Color.Gray,
            modifier = Modifier.padding(top = 8.dp)
        )

        Spacer(modifier = Modifier.height(32.dp))

        // Referencia OXXO
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoNaranja),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier.padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text("NÚMERO DE REFERENCIA", fontWeight = FontWeight.Bold, color = oxxoOrange)
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = oxxoResult?.oxxoNumber ?: "Generando...",
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Black,
                    color = smartvanNavy,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.padding(top = 4.dp)
                )

                if (!oxxoResult?.referencia.isNullOrEmpty()) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Text("Referencia:", fontWeight = FontWeight.SemiBold, color = Color.Gray)
                    Text(
                        oxxoResult!!.referencia,
                        fontWeight = FontWeight.Bold,
                        color = Color.DarkGray
                    )
                }

                HorizontalDivider(modifier = Modifier.padding(vertical = 16.dp))

                Text("Monto a pagar:", fontWeight = FontWeight.SemiBold, color = Color.DarkGray)
                Text(
                    "$$totalToPay MXN",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = smartvanGreen
                )
            }
        }

        // Botón para abrir voucher
        if (!oxxoResult?.voucherUrl.isNullOrBlank()) {
            Spacer(modifier = Modifier.height(12.dp))
            val uriHandler = LocalUriHandler.current
            OutlinedButton(
                onClick = { uriHandler.openUri(oxxoResult!!.voucherUrl) },
                modifier = Modifier.fillMaxWidth().height(48.dp),
                border = androidx.compose.foundation.BorderStroke(2.dp, oxxoOrange),
                shape = RoundedCornerShape(12.dp)
            ) {
                Icon(Icons.Filled.Store, contentDescription = null, tint = oxxoOrange, modifier = Modifier.size(18.dp))
                Spacer(modifier = Modifier.width(8.dp))
                Text("Ver Ficha de Pago OXXO", fontWeight = FontWeight.Bold, color = oxxoOrange)
            }
        }

        Spacer(modifier = Modifier.height(16.dp))

        // Aviso de expiración
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
            shape = RoundedCornerShape(12.dp)
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Text(
                    "Tu asiento queda apartado 24 horas.",
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF6D4C00)
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    "Paga en cualquier OXXO dentro de ese plazo. Si no se registra el pago en 24 horas, el asiento se libera automáticamente y quedará disponible para otros.",
                    color = Color(0xFF6D4C00),
                    style = MaterialTheme.typography.bodySmall
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    "Una vez confirmado tu pago, recibirás tus boletos por correo electrónico automáticamente.",
                    color = Color(0xFF6D4C00),
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }

        // Banner de pago confirmado en tiempo real
        AnimatedVisibility(visible = pagoConfirmado) {
            Card(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                shape = RoundedCornerShape(12.dp)
            ) {
                Row(
                    modifier = Modifier.padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = smartvanGreen, modifier = Modifier.size(28.dp))
                    Spacer(modifier = Modifier.width(12.dp))
                    Column {
                        Text("Pago confirmado", fontWeight = FontWeight.Bold, color = SmartVanColors.VerdeOscuro)
                        Text("Tus boletos fueron enviados a tu correo.", style = MaterialTheme.typography.bodySmall, color = Color(0xFF388E3C))
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(32.dp))

        Button(
            onClick = { onFinishClick() },
            modifier = Modifier.fillMaxWidth().height(50.dp),
            colors = ButtonDefaults.buttonColors(containerColor = if (pagoConfirmado) smartvanGreen else oxxoOrange),
            shape = RoundedCornerShape(12.dp)
        ) {
            Text(if (pagoConfirmado) "Ver mis boletos" else "Entendido", fontWeight = FontWeight.Bold, color = Color.White)
        }
    }
}
